import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiDollarSign, FiDownload, FiPrinter, FiSearch, FiPercent } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { navigateToTransaction } from '../../utils/navigationHelper';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getEmployeesCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { formatCurrency, formatNumber, formatDate as fmtDate } from '../../utils/formatters';
import useReportMemory from '../../hooks/useReportMemory';
import { addPrintBrand } from '../../utils/printBranding';

const formatDate = (v) => fmtDate(v) || '-';

const SalesmanIncentive = () => {
  const { activeCompany, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('salesman-incentive');
  const navigate = useNavigate();

  // Filters
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [selectedEmployee, setSelectedEmployee] = useState(() => savedMem?.selectedEmployee ?? 'all');
  const [taxFilter, setTaxFilter] = useState(() => savedMem?.taxFilter ?? 'both');
  const [searchQuery, setSearchQuery] = useState(() => savedMem?.searchQuery ?? '');

  // Incentive slab rates (editable)
  const [slab1Rate, setSlab1Rate] = useState(() => savedMem?.slab1Rate ?? 1.0);   // 0-30 days
  const [slab2Rate, setSlab2Rate] = useState(() => savedMem?.slab2Rate ?? 0.5);   // 31-90 days
  const [slab3Rate, setSlab3Rate] = useState(() => savedMem?.slab3Rate ?? 0.5);   // Over 90 days

  // Data
  const [employees, setEmployees] = useState([]);
  const [reportData, setReportData] = useState(() => savedMem?.reportData ?? []);
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(() => savedMem?.hasGenerated ?? false);

  // Default date range: current month (only on a fresh visit — a restored
  // snapshot keeps the dates the user last generated with).
  useEffect(() => {
    if (!savedMem) {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(firstDay.toISOString().split('T')[0]);
      setDateTo(today.toISOString().split('T')[0]);
    }
  }, [savedMem]);

  // Load employees
  useEffect(() => {
    if (!activeCompany?.id) return;
    const load = async () => {
      try {
        const empRows = await getEmployeesCached(activeCompany.id);
        setEmployees(empRows.filter(r => r.firstName != null)
          .sort((a, b) => String(a.firstName || '').localeCompare(String(b.firstName || ''))));
      } catch (err) { console.error('Error loading employees:', err); }
    };
    load();
  }, [activeCompany]);

  // Generate report
  const generateReport = useCallback(async () => {
    if (!activeCompany?.id || !dateFrom || !dateTo) return;

    setLoading(true);
    setHasGenerated(true);

    try {
      const transRef = collection(db, 'companies', activeCompany.id, 'transactions');

      // Load all receipts (CR + BR) in the date range
      const [crSnap, brSnap] = await Promise.all([
        getDocs(query(transRef, where('tranTypeCode', '==', 'CR'))),
        getDocs(query(transRef, where('tranTypeCode', '==', 'BR')))
      ]);

      const allReceipts = [...crSnap.docs, ...brSnap.docs]
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(r => r.status !== 'Cancelled' && r.docDate >= dateFrom && r.docDate <= dateTo)
        .filter(r => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return r.branchId === activeBranch.id;
        });

      // Load all sale invoices (for linking) - include both tax and non-tax
      const taxCodes = taxFilter === 'tax' ? ['SI'] : taxFilter === 'nonTax' ? ['NSI'] : ['SI', 'NSI'];
      const siSnap = await getDocs(query(transRef, where('tranTypeCode', 'in', taxCodes)));
      const invoiceMap = {};
      siSnap.docs.forEach(d => {
        const data = d.data();
        invoiceMap[d.id] = {
          id: d.id,
          tranTypeCode: data.tranTypeCode || 'SI',
          docNumber: data.docNumber || '',
          docDate: data.docDate || '',
          partyId: data.partyId || '',
          partyName: data.partyName || '',
          employeeId: data.employeeId || '',
          employeeName: data.employeeName || '',
          totalAmount: data.totalAmount || 0
        };
      });

      // Build report rows: each row = one allocation (receipt → invoice)
      const rows = [];

      allReceipts.forEach(receipt => {
        const allocations = receipt.invoiceAllocations || [];
        if (allocations.length === 0) return;

        allocations.forEach(alloc => {
          const invoice = invoiceMap[alloc.invoiceId];
          if (!invoice) return;

          // Filter by employee
          if (selectedEmployee !== 'all' && invoice.employeeId !== selectedEmployee) return;

          // Calculate aging: days between invoice date and receipt date
          const invDate = new Date(invoice.docDate);
          const recDate = new Date(receipt.docDate);
          const agingDays = Math.max(0, Math.floor((recDate - invDate) / (1000 * 60 * 60 * 24)));

          // Slot into slab
          let slab1 = 0, slab2 = 0, slab3 = 0;
          // Amount collected against the invoice. A write-off (Adjustment / Bad Debt
          // on the receipt) cleared the invoice but was never collected, so it earns
          // no incentive; WHT deducted by the customer still counts as collected.
          const amount = Math.max(0, (alloc.allocatedAmount || 0) - (Number(alloc.adjustmentAmount) || 0));
          if (agingDays <= 30) {
            slab1 = amount;
          } else if (agingDays <= 90) {
            slab2 = amount;
          } else {
            slab3 = amount;
          }

          rows.push({
            employeeId: invoice.employeeId,
            employeeName: invoice.employeeName || 'Unassigned',
            partyName: invoice.partyName || '-',
            invoiceNumber: invoice.docNumber || alloc.docNumber || '-',
            invoiceId: alloc.invoiceId,
            invoiceTranType: invoice.tranTypeCode || 'SI',
            invoiceDate: invoice.docDate || '',
            receiptNumber: receipt.docNumber || '-',
            receiptId: receipt.id,
            receiptTranType: receipt.tranTypeCode,
            receiptDate: receipt.docDate || '',
            agingDays,
            amount,
            slab1,
            slab2,
            slab3
          });
        });
      });

      // Sort by employee, then customer, then invoice date
      rows.sort((a, b) => {
        const empCmp = (a.employeeName || '').localeCompare(b.employeeName || '');
        if (empCmp !== 0) return empCmp;
        const custCmp = (a.partyName || '').localeCompare(b.partyName || '');
        if (custCmp !== 0) return custCmp;
        return (a.invoiceDate || '').localeCompare(b.invoiceDate || '');
      });

      setReportData(rows);
    } catch (err) {
      console.error('Error generating incentive report:', err);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, activeBranch, dateFrom, dateTo, selectedEmployee]);

  // Group data by employee
  const groupedData = useMemo(() => {
    const groups = {};
    const filtered = searchQuery
      ? reportData.filter(r =>
          r.partyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          r.invoiceNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          r.receiptNumber?.toLowerCase().includes(searchQuery.toLowerCase()))
      : reportData;

    filtered.forEach(row => {
      const key = row.employeeId || 'unassigned';
      if (!groups[key]) {
        groups[key] = { employeeName: row.employeeName, rows: [], slab1: 0, slab2: 0, slab3: 0 };
      }
      groups[key].rows.push(row);
      groups[key].slab1 += row.slab1;
      groups[key].slab2 += row.slab2;
      groups[key].slab3 += row.slab3;
    });

    return Object.entries(groups).map(([id, data]) => ({
      employeeId: id,
      ...data,
      incentive1: Math.round(data.slab1 * (slab1Rate / 100) * 100) / 100,
      incentive2: Math.round(data.slab2 * (slab2Rate / 100) * 100) / 100,
      incentive3: Math.round(data.slab3 * (slab3Rate / 100) * 100) / 100,
      totalIncentive: Math.round((data.slab1 * (slab1Rate / 100) + data.slab2 * (slab2Rate / 100) + data.slab3 * (slab3Rate / 100)) * 100) / 100
    }));
  }, [reportData, searchQuery, slab1Rate, slab2Rate, slab3Rate]);

  // Grand totals
  const grandTotals = useMemo(() => {
    return groupedData.reduce((acc, g) => ({
      slab1: acc.slab1 + g.slab1,
      slab2: acc.slab2 + g.slab2,
      slab3: acc.slab3 + g.slab3,
      incentive1: acc.incentive1 + g.incentive1,
      incentive2: acc.incentive2 + g.incentive2,
      incentive3: acc.incentive3 + g.incentive3,
      totalIncentive: acc.totalIncentive + g.totalIncentive,
      totalRecords: acc.totalRecords + g.rows.length
    }), { slab1: 0, slab2: 0, slab3: 0, incentive1: 0, incentive2: 0, incentive3: 0, totalIncentive: 0, totalRecords: 0 });
  }, [groupedData]);

  // Backward memory: keep the generated view (filters + rows) in sync so Back
  // restores it with 0 re-reads. Slab rates and search are display-only over the
  // already-loaded reportData, so we re-save whenever they change (in-memory, ~free).
  useEffect(() => {
    if (!hasGenerated) return;
    saveMemory({
      dateFrom, dateTo, selectedEmployee, taxFilter, searchQuery,
      slab1Rate, slab2Rate, slab3Rate,
      reportData, hasGenerated: true
    });
  }, [hasGenerated, dateFrom, dateTo, selectedEmployee, taxFilter, searchQuery,
      slab1Rate, slab2Rate, slab3Rate, reportData, saveMemory]);

  // CSV Export
  const handleExport = () => {
    if (reportData.length === 0) return;
    let csv = 'Salesman Incentive Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Period: ${dateFrom} to ${dateTo}\n`;
    csv += `Slab Rates: 0-30 days: ${slab1Rate}%, 31-90 days: ${slab2Rate}%, Over 90: ${slab3Rate}%\n\n`;

    groupedData.forEach(group => {
      csv += `\nSalesman: ${group.employeeName}\n`;
      csv += 'Customer,Invoice #,Invoice Date,Receipt #,Receipt Date,Days,0-30 Days,31-90 Days,Over 90\n';
      group.rows.forEach(r => {
        csv += `"${r.partyName}",${r.invoiceNumber},${r.invoiceDate},${r.receiptNumber},${r.receiptDate},${r.agingDays},`;
        csv += `${r.slab1 || ''},${r.slab2 || ''},${r.slab3 || ''}\n`;
      });
      csv += `Total,,,,,,${group.slab1},${group.slab2},${group.slab3}\n`;
      csv += `Incentive,,,,,,${group.incentive1},${group.incentive2},${group.incentive3}\n`;
      csv += `Total Incentive: ${group.totalIncentive}\n`;
    });

    csv += `\nGrand Total,,,,,,${grandTotals.slab1},${grandTotals.slab2},${grandTotals.slab3}\n`;
    csv += `Grand Incentive,,,,,,${grandTotals.incentive1},${grandTotals.incentive2},${grandTotals.incentive3}\n`;
    csv += `Grand Total Incentive: ${grandTotals.totalIncentive}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Salesman_Incentive_${dateFrom}_${dateTo}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print
  const handlePrint = () => {
    if (groupedData.length === 0) return;
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Salesman Incentive Report</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:11px}
        h1{color:#800020;font-size:18px;margin-bottom:3px}
        .meta{color:#666;font-size:11px;margin-bottom:15px}
        .salesman-header{background:#800020;color:white;padding:8px 12px;font-weight:600;font-size:13px;margin-top:20px;border-radius:4px 4px 0 0}
        table{width:100%;border-collapse:collapse;margin-bottom:4px}
        th{background:#f5f5f5;padding:5px 8px;text-align:left;font-size:9px;text-transform:uppercase;border-bottom:1px solid #ddd}
        th.right{text-align:right}
        td{padding:4px 8px;border-bottom:1px solid #eee;font-size:10px}
        td.right{text-align:right;font-family:'Consolas',monospace}
        .totals-row{background:#f5f5f5;font-weight:600}
        .totals-row td{border-top:2px solid #800020}
        .incentive-row{background:#fff8e1;font-weight:500}
        .incentive-row td{color:#B8860B}
        .grand-row{background:#800020;color:white;font-weight:700}
        .grand-row td{padding:8px}
        .slab-header{text-align:center;font-size:10px;color:#666;margin-bottom:10px}
        @media print{body{margin:15px}.salesman-header{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
      </style></head><body>
      <h1>Salesman Incentive Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || ''} | Period: ${formatDate(dateFrom)} to ${formatDate(dateTo)} | Records: ${grandTotals.totalRecords}
      </div>
      <div class="slab-header">
        Incentive Rates — 0-30 days: <strong>${slab1Rate}%</strong> | 31-90 days: <strong>${slab2Rate}%</strong> | Over 90 days: <strong>${slab3Rate}%</strong>
      </div>`);

    groupedData.forEach(group => {
      w.document.write(`
        <div class="salesman-header">Salesman: ${group.employeeName} — Incentive: ${formatCurrency(group.totalIncentive)}</div>
        <table><thead><tr>
          <th>Customer</th><th>Invoice #</th><th>Date</th><th>Receipt #</th><th>Date</th><th class="right">Days</th>
          <th class="right">0-30 Days (${slab1Rate}%)</th><th class="right">31-90 Days (${slab2Rate}%)</th><th class="right">Over 90 (${slab3Rate}%)</th>
        </tr></thead><tbody>`);

      group.rows.forEach(r => {
        w.document.write(`<tr>
          <td>${r.partyName}</td><td>${r.invoiceNumber}</td><td>${formatDate(r.invoiceDate)}</td>
          <td>${r.receiptNumber}</td><td>${formatDate(r.receiptDate)}</td><td class="right">${r.agingDays}</td>
          <td class="right">${r.slab1 ? formatCurrency(r.slab1) : ''}</td>
          <td class="right">${r.slab2 ? formatCurrency(r.slab2) : ''}</td>
          <td class="right">${r.slab3 ? formatCurrency(r.slab3) : ''}</td>
        </tr>`);
      });

      w.document.write(`
        <tr class="totals-row">
          <td colspan="6" style="text-align:right">Total</td>
          <td class="right">${formatCurrency(group.slab1)}</td>
          <td class="right">${formatCurrency(group.slab2)}</td>
          <td class="right">${formatCurrency(group.slab3)}</td>
        </tr>
        <tr class="incentive-row">
          <td colspan="6" style="text-align:right">Incentive</td>
          <td class="right">${formatCurrency(group.incentive1)}</td>
          <td class="right">${formatCurrency(group.incentive2)}</td>
          <td class="right">${formatCurrency(group.incentive3)}</td>
        </tr>
        <tr class="incentive-row">
          <td colspan="8" style="text-align:right;font-weight:700">Total Incentive</td>
          <td class="right" style="font-weight:700;font-size:12px">${formatCurrency(group.totalIncentive)}</td>
        </tr>
        </tbody></table>`);
    });

    w.document.write(`
      <table style="margin-top:20px"><tbody>
        <tr class="grand-row">
          <td colspan="6" style="text-align:right">Grand Total</td>
          <td class="right">${formatCurrency(grandTotals.slab1)}</td>
          <td class="right">${formatCurrency(grandTotals.slab2)}</td>
          <td class="right">${formatCurrency(grandTotals.slab3)}</td>
        </tr>
        <tr class="grand-row">
          <td colspan="6" style="text-align:right">Grand Incentive</td>
          <td class="right">${formatCurrency(grandTotals.incentive1)}</td>
          <td class="right">${formatCurrency(grandTotals.incentive2)}</td>
          <td class="right">${formatCurrency(grandTotals.incentive3)}</td>
        </tr>
        <tr class="grand-row">
          <td colspan="8" style="text-align:right;font-size:13px">Grand Total Incentive</td>
          <td class="right" style="font-size:14px">${formatCurrency(grandTotals.totalIncentive)}</td>
        </tr>
      </tbody></table>
      <div style="font-size:10px;color:#999;margin-top:15px;text-align:center">Generated on ${new Date().toLocaleString()} | AMAL ERP</div>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  return (
    <PageLayout pageTitle="Salesman Incentive" breadcrumb="Reports / Sale Reports / Salesman Incentive">
      {/* Filters */}
      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>From Date</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>To Date</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>Salesman</Label>
            <Select value={selectedEmployee} onChange={e => setSelectedEmployee(e.target.value)}>
              <option value="all">All Salesmen</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.employeeCode ? emp.employeeCode + ' - ' : ''}{emp.firstName} {emp.lastName}
                </option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Tax Filter</Label>
            <Select value={taxFilter} onChange={e => setTaxFilter(e.target.value)}>
              <option value="both">Both (Tax + Non-Tax)</option>
              <option value="tax">Tax Only</option>
              <option value="nonTax">Non-Tax Only</option>
            </Select>
          </FilterGroup>
          <SlabGroup>
            <Label>0-30 Days %</Label>
            <SlabInput type="number" min="0" max="100" step="0.1" value={slab1Rate} onChange={e => setSlab1Rate(Number(e.target.value) || 0)} />
          </SlabGroup>
          <SlabGroup>
            <Label>31-90 Days %</Label>
            <SlabInput type="number" min="0" max="100" step="0.1" value={slab2Rate} onChange={e => setSlab2Rate(Number(e.target.value) || 0)} />
          </SlabGroup>
          <SlabGroup>
            <Label>Over 90 %</Label>
            <SlabInput type="number" min="0" max="100" step="0.1" value={slab3Rate} onChange={e => setSlab3Rate(Number(e.target.value) || 0)} />
          </SlabGroup>
          <ButtonGroup>
            <GenerateButton onClick={generateReport} disabled={loading}>
              {loading ? 'Generating...' : 'Generate Report'}
            </GenerateButton>
            <Button onClick={handleExport} disabled={reportData.length === 0}><FiDownload /> CSV</Button>
            <Button onClick={handlePrint} disabled={groupedData.length === 0}><FiPrinter /> Print</Button>
          </ButtonGroup>
        </FiltersRow>
        {hasGenerated && reportData.length > 0 && (
          <SearchRow>
            <FiSearch />
            <SearchField type="text" placeholder="Search by customer, invoice# or receipt#..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          </SearchRow>
        )}
      </FiltersCard>

      {/* Summary Cards */}
      {hasGenerated && !loading && groupedData.length > 0 && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIcon $color={colors.primary.main}><FiDollarSign /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Collections</SummaryLabel>
              <SummaryValue>{formatCurrency(grandTotals.slab1 + grandTotals.slab2 + grandTotals.slab3)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon $color={colors.success.main}><FiPercent /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>0-30 Days ({slab1Rate}%)</SummaryLabel>
              <SummaryValue>{formatCurrency(grandTotals.slab1)}</SummaryValue>
              <SummarySub>Incentive: {formatCurrency(grandTotals.incentive1)}</SummarySub>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon $color={colors.warning.main}><FiPercent /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>31-90 Days ({slab2Rate}%)</SummaryLabel>
              <SummaryValue>{formatCurrency(grandTotals.slab2)}</SummaryValue>
              <SummarySub>Incentive: {formatCurrency(grandTotals.incentive2)}</SummarySub>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon $color={colors.error.main}><FiPercent /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Over 90 Days ({slab3Rate}%)</SummaryLabel>
              <SummaryValue>{formatCurrency(grandTotals.slab3)}</SummaryValue>
              <SummarySub>Incentive: {formatCurrency(grandTotals.incentive3)}</SummarySub>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard $highlight>
            <SummaryIcon $color="#800020"><FiDollarSign /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Incentive</SummaryLabel>
              <SummaryValue style={{ color: '#800020', fontSize: 22 }}>{formatCurrency(grandTotals.totalIncentive)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* Data Table */}
      <ContentCard>
        {loading ? (
          <EmptyState>Loading report data...</EmptyState>
        ) : !hasGenerated ? (
          <EmptyState>Select date range, salesman and slab rates, then click "Generate Report"</EmptyState>
        ) : groupedData.length === 0 ? (
          <EmptyState>No incentive data found for the selected criteria. Ensure receipts have invoice allocations.</EmptyState>
        ) : (
          <TableContainer>
            {groupedData.map(group => (
              <SalesmanSection key={group.employeeId}>
                <SalesmanHeader>
                  <span>Salesman: <strong>{group.employeeName}</strong></span>
                  <span>Records: {group.rows.length} | Incentive: <strong>{formatCurrency(group.totalIncentive)}</strong></span>
                </SalesmanHeader>
                <Table>
                  <Thead>
                    <tr>
                      <Th>Customer</Th>
                      <Th>Invoice #</Th>
                      <Th>Inv Date</Th>
                      <Th>Receipt #</Th>
                      <Th>Rcpt Date</Th>
                      <Th $right>Days</Th>
                      <Th $right>0-30 Days</Th>
                      <Th $right>31-90 Days</Th>
                      <Th $right>Over 90</Th>
                    </tr>
                  </Thead>
                  <tbody>
                    {group.rows.map((row, idx) => (
                      <Tr key={idx}>
                        <Td>{row.partyName}</Td>
                        <Td><DocNum onClick={() => row.invoiceId && navigateToTransaction(navigate, row.invoiceTranType, row.invoiceId)} title="Open invoice" style={{ cursor: row.invoiceId ? 'pointer' : 'default', color: colors.primary.main }}>{row.invoiceNumber}</DocNum></Td>
                        <Td>{formatDate(row.invoiceDate)}</Td>
                        <Td><DocNum onClick={() => row.receiptId && navigateToTransaction(navigate, row.receiptTranType, row.receiptId)} title="Open receipt" style={{ cursor: row.receiptId ? 'pointer' : 'default', color: colors.primary.main }}>{row.receiptNumber}</DocNum></Td>
                        <Td>{formatDate(row.receiptDate)}</Td>
                        <Td $right $mono>{row.agingDays}</Td>
                        <Td $right $mono $highlight={row.slab1 > 0}>{row.slab1 ? formatCurrency(row.slab1) : ''}</Td>
                        <Td $right $mono $highlight={row.slab2 > 0}>{row.slab2 ? formatCurrency(row.slab2) : ''}</Td>
                        <Td $right $mono $highlight={row.slab3 > 0}>{row.slab3 ? formatCurrency(row.slab3) : ''}</Td>
                      </Tr>
                    ))}
                    <TotalsRow>
                      <Td colSpan={6} style={{ textAlign: 'right', fontWeight: 700 }}>Total</Td>
                      <Td $right $mono>{formatCurrency(group.slab1)}</Td>
                      <Td $right $mono>{formatCurrency(group.slab2)}</Td>
                      <Td $right $mono>{formatCurrency(group.slab3)}</Td>
                    </TotalsRow>
                    <IncentiveRow>
                      <Td colSpan={6} style={{ textAlign: 'right' }}>Incentive ({slab1Rate}% / {slab2Rate}% / {slab3Rate}%)</Td>
                      <Td $right $mono>{formatCurrency(group.incentive1)}</Td>
                      <Td $right $mono>{formatCurrency(group.incentive2)}</Td>
                      <Td $right $mono>{formatCurrency(group.incentive3)}</Td>
                    </IncentiveRow>
                    <IncentiveTotalRow>
                      <Td colSpan={8} style={{ textAlign: 'right', fontWeight: 700 }}>Total Incentive</Td>
                      <Td $right $mono style={{ fontWeight: 700, fontSize: 14 }}>{formatCurrency(group.totalIncentive)}</Td>
                    </IncentiveTotalRow>
                  </tbody>
                </Table>
              </SalesmanSection>
            ))}
          </TableContainer>
        )}
      </ContentCard>
    </PageLayout>
  );
};

// =============== STYLED COMPONENTS ===============

const FiltersCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 140px;
`;

const SlabGroup = styled(FilterGroup)`
  min-width: 90px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
`;

const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: 6px;
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
`;

const Input = styled.input`
  padding: 8px 10px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: 6px;
  font-size: 13px;
  color: ${colors.text.primary};
`;

const SlabInput = styled(Input)`
  width: 70px;
  text-align: center;
  font-weight: 600;
  color: #800020;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const Button = styled.button`
  padding: 8px 14px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: 6px;
  font-size: 13px;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  &:hover { background: ${colors.neutral[50]}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const GenerateButton = styled(Button)`
  background: #800020;
  color: white;
  border-color: #800020;
  font-weight: 600;
  &:hover { background: #600018; }
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${colors.neutral[200]};
  color: ${colors.text.secondary};
`;

const SearchField = styled.input`
  flex: 1;
  padding: 8px 10px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: 6px;
  font-size: 13px;
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: ${props => props.$highlight ? '#800020' + '08' : 'white'};
  border: ${props => props.$highlight ? '2px solid #800020' : '1px solid ' + colors.neutral[200]};
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
`;

const SummaryIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;

const SummaryInfo = styled.div``;

const SummaryLabel = styled.div`
  font-size: 11px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;
`;

const SummaryValue = styled.div`
  font-size: 17px;
  font-weight: 600;
  color: ${colors.text.primary};
`;

const SummarySub = styled.div`
  font-size: 11px;
  color: #B8860B;
  font-weight: 500;
  margin-top: 2px;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const SalesmanSection = styled.div`
  margin-bottom: 4px;
`;

const SalesmanHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: #800020;
  color: white;
  font-size: 13px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 8px 12px;
  text-align: ${props => props.$right ? 'right' : 'left'};
  font-size: 10px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  &:hover { background: ${colors.neutral[50]}; }
`;

const TotalsRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid #800020;
`;

const IncentiveRow = styled.tr`
  background: #FFF8E1;
  td { color: #B8860B; }
`;

const IncentiveTotalRow = styled.tr`
  background: #FFF3E0;
  td { color: #800020; }
`;

const Td = styled.td`
  padding: 6px 12px;
  font-size: 12px;
  text-align: ${props => props.$right ? 'right' : 'left'};
  font-family: ${props => props.$mono ? "'SF Mono','Consolas',monospace" : 'inherit'};
  color: ${props => props.$highlight ? colors.text.primary : 'inherit'};
  font-weight: ${props => props.$highlight ? 500 : 'inherit'};
`;

const DocNum = styled.span`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 11px;
  color: ${colors.primary.main};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
  font-size: 14px;
`;

export default SalesmanIncentive;
